import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { isAdminDomain, redirectToDashboard, redirectToAdmin } from '../../lib/domain';

export function ClientProtectedRoute({ children }: { children: React.ReactNode }) {
  const { slug } = useParams<{ slug: string }>();
  const { user, loading, userRoles, isSuperAdmin, signOut } = useAuth();
  const { organizations } = useOrganization();

  // Client routes are not available on admin domain — redirect to dashboard domain
  if (isAdminDomain()) {
    redirectToDashboard(window.location.pathname);
    return null;
  }

  // Show spinner on initial load OR when roles haven't been fetched yet.
  // On session refresh (visibilitychange / online), user and userRoles already
  // have previous values so we skip the spinner to avoid unmounting the page tree
  // (which would lose modals, drawers, form data, scroll position).
  if (loading && (!user || userRoles.length === 0)) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Super admins never operate client routes from the dashboard domain — catches
  // an already-active session (bookmark, deep link) that predates this gate.
  if (isSuperAdmin) {
    signOut().then(() => redirectToAdmin());
    return null;
  }

  // Check if org is suspended
  if (slug) {
    const org = organizations.find(o => o.slug === slug);
    if (org?.subscription_status === 'suspended') {
      return (
        <div className="min-h-dvh bg-black flex items-center justify-center p-6">
          <div className="bg-white border border-stone rounded-3xl p-10 max-w-md w-full text-center space-y-6 shadow-2xl">
            <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto text-amber-500 shadow-inner">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-black text-ink uppercase tracking-tight">Compte suspendu</h1>
              <p className="text-[13px] text-gray-400 mt-2 leading-relaxed">
                Votre accès a été temporairement désactivé. Contactez votre administrateur pour plus d'informations.
              </p>
            </div>
          </div>
        </div>
      );
    }
  }

  // Check if user belongs to this organization
  const hasAccess = userRoles.some((role) => role.organizationSlug === slug);

  if (!hasAccess) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}
